// @ts-ignore
import React from 'react';
// @ts-ignore
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { checkAuth } from '../../utils/authUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.startsWith('http') ? API_URL : '';

interface ProtectedRouteProps {
  redirectPath?: string;
  isAdminRoute?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  redirectPath = '/',
  isAdminRoute = false
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [wasSubAdmin, setWasSubAdmin] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    const verifyAuth = async () => {
      if (isAdminRoute) {
        // VULN-009 FIX: Server-side admin auth verification instead of localStorage
        try {
          const response = await fetch(`${API_BASE}/api/admin/auth/check`, {
            method: 'GET',
            credentials: 'include', // Send cookies (admin_token / subadmin_token)
            headers: { 'Accept': 'application/json' },
          });

          if (!response.ok) {
            console.log('[ProtectedRoute] Admin auth check failed with status:', response.status);
            // Detect sub-admin to redirect to correct login page
            const subAdminAuth = localStorage.getItem('subAdminAuth');
            if (subAdminAuth === 'true') {
              setWasSubAdmin(true);
              localStorage.removeItem('subAdminAuth');
              localStorage.removeItem('subAdminInfo');
            }
            setIsAuthenticated(false);
            setIsLoading(false);
            return;
          }

          const data = await response.json();
          console.log('[ProtectedRoute] Admin auth check result:', data.authenticated);
          if (data.authenticated === true) {
            setIsAuthenticated(true);
          } else {
            // Auth failed — detect if user was a sub-admin to redirect appropriately
            const subAdminAuth = localStorage.getItem('subAdminAuth');
            if (subAdminAuth === 'true') {
              setWasSubAdmin(true);
              localStorage.removeItem('subAdminAuth');
              localStorage.removeItem('subAdminInfo');
            }
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('[ProtectedRoute] Admin auth verification error:', error);
          const subAdminAuth = localStorage.getItem('subAdminAuth');
          if (subAdminAuth === 'true') {
            setWasSubAdmin(true);
            localStorage.removeItem('subAdminAuth');
            localStorage.removeItem('subAdminInfo');
          }
          setIsAuthenticated(false);
        }
      } else {
        // For user routes, use cookie-based authentication
        try {
          // If admin is editing/viewing an application via admin flow, verify admin cookie server-side
          const adminEditMode = sessionStorage.getItem('adminEditMode');
          if (adminEditMode === 'true') {
            const response = await fetch(`${API_BASE}/api/admin/auth/check`, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Accept': 'application/json' },
            });
            if (response.ok) {
              const data = await response.json();
              if (data.authenticated === true) {
                setIsAuthenticated(true);
                setIsLoading(false);
                return;
              }
            }
            // Admin edit mode flag set but not authenticated — clear it
            sessionStorage.removeItem('adminEditMode');
          }

          // Standard user auth check via server-side cookie verification
          console.log('[ProtectedRoute] Checking user auth via API');
          const { authenticated } = await checkAuth();
          console.log('[ProtectedRoute] Auth check result:', authenticated);
          setIsAuthenticated(authenticated);
        } catch (error) {
          console.error('[ProtectedRoute] Auth verification error:', error);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };

    verifyAuth();
  }, [isAdminRoute]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  // If not authenticated, redirect to appropriate login page
  if (!isAuthenticated) {
    // Sub-admins should be redirected to their own login page, not the admin login
    const actualRedirectPath = (isAdminRoute && wasSubAdmin) ? '/su-admin/login' : redirectPath;
    return <Navigate to={actualRedirectPath} replace state={{ from: location }} />;
  }

  // If authenticated, render the child routes
  return <Outlet />;
};

export default ProtectedRoute;
